import { createRouter, createWebHistory } from 'vue-router'
import Auth from '../views/Auth.vue'
import Login from '../components/Auth/Login.vue'
import PwResetRequest from '../components/Auth/PwResetRequest.vue'
import PwReset from '../components/Auth/PwReset.vue'

import UserIndex from '../views/Authenticated/UserIndex.vue'

/////////////////// AUTHENTICATED /////////////////////////
import Dashboard from '../views/Authenticated/Dashboard.vue'

// QUERY VIEWS
import Query from '../views/Authenticated/Query/Query.vue'
import Enrich from '../views/Authenticated/Enrich/Enrich.vue'

// SIGNALS
import Signals from '../views/Authenticated/Signals/Signals.vue'

// NOTIFICATIONS
import Notifications from '../views/Authenticated/Notifications.vue'

// GUIDES
import Guides from '../views/Authenticated/Guides.vue'

// ANONYMIZATION (authenticated admin views)
import Providers from '../views/Providers.vue'
import ProviderProfile from '../views/ProviderProfile.vue'

// EVENTS (new layout with nested routes)
import EventSearchLayout from '../views/Authenticated/EventSearch/EventSearchLayout.vue'
import EventSearchLandingView from '../views/Authenticated/EventSearch/LandingView.vue'
import EventSearchDefaultView from '../views/Authenticated/EventSearch/DefaultView.vue'
import EventSearchQueryHistory from '../views/Authenticated/EventSearch/QueryHistoryView.vue'

import QueryRunner from '../views/Authenticated/Query/Runner.vue'

// ACCOUNT
import AccountHome from '../views/Authenticated/Account/Home.vue'
import AccountInfo from '../views/Authenticated/Account/Information.vue'
import AccountApiKeys from '../views/Authenticated/Account/ApiKeys.vue'

// DOCUMENTATION VIEWS
import DocsIndex from '../views/Authenticated/Docs/Index.vue'
import ApiReferenceAuthentication from '../views/Authenticated/Docs/Api/Pages/Authentication.vue'
import ApiReferenceFeeds from '../views/Authenticated/Docs/Api/Pages/Feeds.vue'
import ApiReferenceNews from '../views/Authenticated/Docs/Api/Pages/News.vue'
import ApiReferenceUsage from '../views/Authenticated/Docs/Api/Pages/Usage.vue'
import ApiReferenceIndex from '../views/Authenticated/Docs/Api/Index.vue'

import ApiReferenceAssetUrl from '../views/Authenticated/Docs/Api/Pages/Assets/Url.vue'
import ApiReferenceAssetArtifact from '../views/Authenticated/Docs/Api/Pages/Assets/Artifact.vue'
import ApiReferenceAssetIp from '../views/Authenticated/Docs/Api/Pages/Assets/Ip.vue'
import ApiReferenceAssetHash from '../views/Authenticated/Docs/Api/Pages/Assets/Hash.vue'

import { useUserStore } from '../stores/user'

// Decode search input from URL parameter
// Handles arrays (comma-separated) and single values
function decodeSearchInput(input) {
  if (!input) return []

  try {
    // If it looks like a comma-separated list, split it
    if (typeof input === 'string' && input.includes(',')) {
      return input.split(',').map(item => decodeURIComponent(item.trim()))
    }
    // Single value
    return [decodeURIComponent(input)]
  } catch (e) {
    console.error('Failed to decode search input:', e)
    return []
  }
}

const routes = [
  // Auth routes (unauthenticated users)
  {
    path: '/',
    component: Auth,
    children: [
      {
        path: '',
        redirect: 'login'
      },
      {
        path: 'login',
        name: 'Login',
        component: Login,
      },
      {
        path: 'password-reset/:token',
        name: 'PasswordReset',
        component: PwReset,
        props: true,
      },
      {
        path: 'pw-reset-request',
        name: 'PwResetRequest',
        component: PwResetRequest,
      },
    ]
  },
  // Authenticated routes
  {
    path: '',
    name: 'UserIndex',
    component: UserIndex,
    meta: {
      requiresAuth: true
    },
    children: [
      {
        path: '',
        redirect: '/dashboard'
      },
      {
        path: 'enrich',
        name: 'Enrich',
        component: Enrich,
      },
      {
        path: 'search',
        name: 'Search',
        component: Query,
      },
      {
        path: 'enrich/ip',
        name: 'EnrichIpResults',
        component: () => import('../views/Authenticated/Enrich/EnrichIpResults.vue'),
        props: route => ({
          input: decodeSearchInput(route.query.input),
          log: route.query.log !== 'false'
        })
      },
      {
        path: 'enrich/ip/table',
        name: 'EnrichIpTable',
        component: () => import('../views/Authenticated/Enrich/EnrichIpTable.vue'),
        props: route => ({
          input: decodeSearchInput(route.query.input),
          log: route.query.log !== 'false'
        })
      },
      {
        path: 'enrich/domain',
        name: 'EnrichDomainResults',
        component: () => import('../views/Authenticated/Enrich/EnrichDomainResults.vue'),
        props: route => ({
          input: decodeSearchInput(route.query.input),
          log: route.query.log !== 'false'
        })
      },
      {
        path: 'enrich/domain/table',
        name: 'EnrichDomainTable',
        component: () => import('../views/Authenticated/Enrich/EnrichDomainTable.vue'),
        props: route => ({
          input: decodeSearchInput(route.query.input),
          log: route.query.log !== 'false'
        })
      },
      {
        path: 'search/ip',
        name: 'SearchResults',
        component: () => import('../views/Authenticated/Search/SearchResults.vue'),
        props: route => ({
          input: decodeSearchInput(route.query.input),
          type: route.query.type,
          log: route.query.log !== 'false' // default to true unless explicitly false
        })
      },
      {
        path: 'providers',
        name: 'Providers',
        component: Providers,
        meta: {
          requiresAdmin: true
        }
      },
      {
        path: 'providers/:name',
        name: 'ProviderProfile',
        component: ProviderProfile,
        props: true,
        meta: {
          requiresAdmin: true
        }
      },
      {
        path: 'admin/providers',
        name: 'AdminProviders',
        component: () => import('../views/Authenticated/AdminProviders.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'search/event',
        component: EventSearchLayout,
        children: [
          {
            path: '',
            name: 'EventSearch',
            component: EventSearchLandingView
          },
          {
            path: 'table',
            name: 'EventSearchTable',
            component: EventSearchDefaultView
          },
          {
            path: 'history',
            name: 'EventSearchHistory',
            component: EventSearchQueryHistory
          },
          {
            path: 'aggregations',
            name: 'EventSearchAggregations',
            component: () => import('../views/Authenticated/EventSearch/AggregationsView.vue')
          }
        ]
      },
      {
        // Backwards compatibility - redirect old routes
        path: 'event-search',
        redirect: to => ({
          path: '/search/event',
          query: to.query
        })
      },
      {
        path: 'event/search',
        redirect: to => ({
          path: '/search/event',
          query: to.query
        })
      },
      {
        path: 'saved-searches',
        name: 'SavedSearches',
        component: () => import('../views/Authenticated/SavedSearches.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: Dashboard,
        meta: {
          requiresAuth: true
        }
      },
      {
        path: 'channels',
        name: 'ChannelsFeed',
        component: () => import('../views/Authenticated/ChannelsFeedView.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'admin/accounts',
        name: 'AdminAccounts',
        component: () => import('../views/Authenticated/AdminAccounts.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'admin/organizations',
        name: 'AdminOrganizations',
        component: () => import('../views/Authenticated/AdminOrganizations.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'admin/notifications',
        name: 'AdminNotifications',
        component: () => import('../views/Authenticated/AdminNotifications.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'admin/tickets',
        name: 'AdminTickets',
        component: () => import('../views/Authenticated/AdminTickets.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'admin/articles',
        name: 'AdminArticles',
        component: () => import('../views/Authenticated/AdminArticles.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'admin/articles/edit/:id?',
        name: 'AdminArticleEditor',
        component: () => import('../views/Authenticated/AdminArticleEditor.vue'),
        props: true,
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'articles',
        name: 'Articles',
        component: () => import('../views/Authenticated/ArticlesView.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        path: 'tickets',
        name: 'Tickets',
        component: () => import('../views/Authenticated/Tickets.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        path: 'rfi',
        name: 'RFI',
        component: () => import('../views/Authenticated/RFI.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        path: 'signals',
        name: 'Signals',
        component: Signals,
        props: true
      },
      {
        path: 'notifications',
        name: 'Notifications',
        component: Notifications,
        meta: {
          requiresAuth: true
        }
      },
      {
        path: 'guides',
        name: 'Guides',
        component: Guides,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: "runner",
        component: QueryRunner,
        name: "QueryRunner",
        props: true
      },
      {
        path: 'account',
        name: 'Account',
        component: AccountHome,
        children: [
          {
            path: '',
            name: 'AccountInfo',
            component: AccountInfo,
            children: []
          },
          {
            path: 'apikeys',
            name: 'AccountApiKeys',
            component: AccountApiKeys,
            children: []
          },
        ]
      },
      {
        path: 'sources',
        component: () => import('../views/Authenticated/Sources/SourcesLayout.vue'),
        meta: {
          requiresAuth: true
        },
        children: [
          {
            path: '',
            name: 'SourcesGraph',
            component: () => import('../views/Authenticated/Sources/GraphView.vue'),
          },
          {
            path: 'list',
            name: 'SourcesList',
            component: () => import('../views/Authenticated/Sources/SourcesView.vue'),
          },
          {
            path: 'enrichments',
            name: 'SourcesEnrichments',
            component: () => import('../views/Authenticated/Sources/EnrichmentsView.vue'),
          },
        ]
      },
      {
        path: 'docs',
        name: 'DocsIndex',
        component: DocsIndex,
        children: [
          {
            path: 'api',
            name: 'UserApiReference',
            component: ApiReferenceIndex,
            children:[
              {
                path: 'feeds',
                name: 'ApiReferenceFeeds',
                component: ApiReferenceFeeds,
              },
              {
                path: 'usage',
                name: 'ApiReferenceUsage',
                component: ApiReferenceUsage,
              },
              {
                path: 'authentication',
                name: 'ApiReferenceAuthentication',
                component: ApiReferenceAuthentication,
              },
              {
                path: 'news',
                name: 'ApiReferenceNews',
                component: ApiReferenceNews,
              },

              {
                path: 'asset/url',
                name: 'ApiReferenceUrl',
                component: ApiReferenceAssetUrl,
              },
              {
                path: 'asset/hash',
                name: 'ApiReferenceHash',
                component: ApiReferenceAssetHash,
              },
              {
                path: 'asset/artifact',
                name: 'ApiReferenceArtifact',
                component: ApiReferenceAssetArtifact,
              },
              {
                path: 'asset/ip',
                name: 'ApiReferenceIp',
                component: ApiReferenceAssetIp,
              },
            ]
          },
        ]
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    redirect: { name: 'Search' }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  path: process.env.VUE_APP_PATH,
  routes
})

router.beforeEach( (to, from, next) => {
  let userStore = useUserStore()

  // Admin routes check (most restrictive)
  if (to.matched.some(record => record.meta.requiresAdmin)) {
    if (!userStore.loggedIn) {
      return next({ name: 'Login', query: { redirect: to.fullPath } })
    }
    if (userStore.accountType !== 1337) {
      return next({ name: 'Dashboard' })
    }
    return next()
  }

  // Authenticated routes check
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (userStore.loggedIn) {
      return next()
    } else {
      return next({ name: 'Login', query: { redirect: to.fullPath } })
    }
  }

  // Login/auth pages - redirect to dashboard if already logged in
  if (to.name === 'Login' || to.name === 'PwResetRequest') {
    if (userStore.loggedIn) {
      return next({ name: 'Dashboard' })
    }
  }

  // Default - proceed
  return next()
});

export default router
